import { WbsItemStatus } from '../../../domain/entity/WbsItemEntity'
import { getWbsItemStatusLabel } from '../../../lib/functions/wbsItem'
import { colorPalette } from '../../style/colorPalette'
import { StatusChip } from '../../components/data-display/StatusChip'
import { styled } from '@mui/material'

// TODO
// Possibility to keep color in DB in the future
// 配色は StatusComboBox からも参照されるため、ここを単一ソースとして維持する。
export const getOptionStyles = (value: string) => {
  switch (value) {
    case WbsItemStatus.TODO:
      return {
        backgroundColor: `${colorPalette.monotone[1]}`,
        textColor: `${colorPalette.monotone[4]}`,
        borderColor: `1px solid ${colorPalette.monotone[2]}`,
      }
    case WbsItemStatus.DOING:
      return {
        backgroundColor: `${colorPalette.yellow[1]}`,
        textColor: `${colorPalette.yellow[4]}`,
        borderColor: `1px solid ${colorPalette.yellow[2]}`,
      }
    case WbsItemStatus.REVIEW:
      return {
        backgroundColor: `${colorPalette.green[1]}`,
        textColor: `${colorPalette.green[7]}`,
        borderColor: `1px solid ${colorPalette.green[2]}`,
      }
    case WbsItemStatus.DONE:
      return {
        backgroundColor: `${colorPalette.skyBlue[1]}`,
        textColor: `${colorPalette.skyBlue[7]}`,
        borderColor: `1px solid ${colorPalette.skyBlue[2]}`,
      }
    case WbsItemStatus.DISCARD:
      return {
        backgroundColor: `${colorPalette.monotone[3]}`,
        textColor: `${colorPalette.monotone[0]}`,
        borderColor: `1px solid ${colorPalette.monotone[3]}`,
      }
    default:
      return {
        backgroundColor: `${colorPalette.monotone[0]}`,
        textColor: `${colorPalette.monotone[10]}`,
        borderColor: `${colorPalette.monotone[10]}`,
      }
  }
}

type MarkerStyle = { color: string; borderColor?: string }

// ●などの小さな図形は塗りつぶし1色で描くため、チップの背景色(淡色)ではなく識別しやすい濃色を使う。
// TODO は未着手であることが伝わるよう淡色で塗る。濃色(monotone[4])だと DISCARD のグレー
// (monotone[3])と隣り合う明度で見分けられない。塗りより一段濃い枠線で輪郭を出す。
// DISCARD だけは文字色が白のため、代わりに背景色(グレー)を採用する。
const getSolidMarkerStyle = (value: string): MarkerStyle => {
  switch (value) {
    case WbsItemStatus.TODO:
      return {
        color: colorPalette.monotone[2],
        borderColor: colorPalette.monotone[3],
      }
    case WbsItemStatus.DISCARD:
      return { color: getOptionStyles(value).backgroundColor }
    default:
      return { color: getOptionStyles(value).textColor }
  }
}

// チップと並べて使う場面では、チップと同じ淡色で塗って見た目を揃える。
// 淡色のままでは輪郭が背景に埋もれるため、枠線には塗りつぶし用の濃色を使う。
const getChipMarkerStyle = (value: string): MarkerStyle => ({
  color: getOptionStyles(value).backgroundColor,
  borderColor: getSolidMarkerStyle(value).color,
})

const StatusMarker = styled('span', {
  shouldForwardProp: prop =>
    prop !== 'markerColor' && prop !== 'size' && prop !== 'markerBorderColor',
})<{ markerColor: string; size: number; markerBorderColor?: string }>(
  ({ markerColor, size, markerBorderColor }) => ({
    display: 'inline-block',
    flexShrink: 0,
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    backgroundColor: markerColor,
    // 枠線を足しても直径が変わらないようにする。
    boxSizing: 'border-box',
    border: markerBorderColor ? `1px solid ${markerBorderColor}` : undefined,
  })
)

type WbsItemStatusMarkerProps = {
  status: WbsItemStatus
  /** ●の直径(px)。既定 8px */
  size?: number
  /** 'chip': チップと同じ淡色で塗る。既定の 'solid' は識別しやすい濃色で塗る。 */
  colorVariant?: 'solid' | 'chip'
  /** 外側で Tooltip を出す場合に、ネイティブの title が二重に出るのを防ぐ。 */
  disableTitle?: boolean
}

// ステータスを色だけで表す●。ラベルを置く余白がない箇所（サブステータス列など）で使う。
export const WbsItemStatusMarker = ({
  status,
  size = 8,
  colorVariant = 'solid',
  disableTitle = false,
}: WbsItemStatusMarkerProps) => {
  const { color, borderColor } =
    colorVariant === 'chip'
      ? getChipMarkerStyle(status)
      : getSolidMarkerStyle(status)
  return (
    <StatusMarker
      markerColor={color}
      markerBorderColor={borderColor}
      size={size}
      title={
        disableTitle ? undefined : (getWbsItemStatusLabel(status) ?? status)
      }
    />
  )
}

type WbsItemStatusChipProps = {
  status: WbsItemStatus
  /** 文字数に応じて幅を最適化する（既定 false: 最小幅 77px） */
  fitContent?: boolean
}

// WBSアイテムのステータス（TODO/DOING/REVIEW/DONE/DISCARD）を共通チップで表示する。
export const WbsItemStatusChip = ({
  status,
  fitContent,
}: WbsItemStatusChipProps) => {
  const { backgroundColor, textColor, borderColor } = getOptionStyles(status)
  return (
    <StatusChip
      label={getWbsItemStatusLabel(status) ?? status}
      backgroundColor={backgroundColor}
      textColor={textColor}
      borderColor={borderColor}
      fitContent={fitContent}
    />
  )
}
